import { Component } from '@theme/component';

/**
 * @typedef {object} CountrySelectorRefs
 * @property {HTMLSelectElement} countries
 * @property {HTMLSelectElement} languages
 * @property {HTMLSelectElement} [currencies]
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} countryInput
 * @property {HTMLInputElement} languageInput
 *
 * @extends {Component<CountrySelectorRefs>}
 */
class CountrySelectorComponent extends Component {
  requiredRefs = ['countries', 'languages', 'form', 'countryInput', 'languageInput'];

  // Safety-net ISO country codes for mainland Europe + Ireland (excludes GB/UK and ANZ, which
  // already resolve via their own dedicated markets). Used only when neither the exact dropdown
  // match nor the merchant-configured country-to-market map resolves a detected country.
  static #EUROPE_FALLBACK_ISO_CODES = new Set([
    'AD', 'AL', 'AT', 'AX', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES',
    'FI', 'FO', 'FR', 'GI', 'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MC',
    'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'SE', 'SI', 'SK', 'SM', 'UA',
    'VA',
  ]);

  #isProgrammaticChange = false;

  connectedCallback() {
    super.connectedCallback();
    this.#applyMarketSelection(undefined, undefined, false);
    this.#checkCacheMismatch();
    this.#onCountryChange();

    if (this.refs.countries) {
      this.refs.countries.addEventListener('change', this.#onCountryChange);
      this.refs.countries.addEventListener('change', this.#onUserCountryChange);
    }

    if (this.refs.languages) {
      this.refs.languages.addEventListener('change', this.#onUserLanguageChange);
    }

    if (this.refs.currencies) {
      this.refs.currencies.addEventListener('change', this.#onCurrencyChange);
    }

    document.addEventListener('dialog:open', this.#onDocumentDialogOpen, true);

    requestAnimationFrame(() => {
      this.#checkUrlCurrencyParam();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('dialog:open', this.#onDocumentDialogOpen, true);
    this.refs.countries?.removeEventListener('change', this.#onCountryChange);
    this.refs.countries?.removeEventListener('change', this.#onUserCountryChange);
    this.refs.languages?.removeEventListener('change', this.#onUserLanguageChange);
    this.refs.currencies?.removeEventListener('change', this.#onCurrencyChange);
    this.#geoGuardCleanup?.();
  }

  #onUserCountryChange = () => {
    if (this.#isProgrammaticChange) return;
    try {
      if (this.refs.countries?.value) {
        sessionStorage.setItem('otter_user_selected_market', this.refs.countries.value);
      }
    } catch (e) {}
  };

  #onUserLanguageChange = () => {
    if (this.#isProgrammaticChange) return;
    try {
      if (this.refs.languages?.value) {
        sessionStorage.setItem('otter_user_selected_language', this.refs.languages.value);
      }
    } catch (e) {}
  };

  #onDocumentDialogOpen = (e) => {
    const host = e.target;

    if (!(host instanceof HTMLElement) || host.id !== 'drawer-country-language-dialog') {
      return;
    }

    if (!this.isConnected) {
      return;
    }

    if (!host.contains(this)) {
      return;
    }

    const w = /** @type {Window & { __otterGeoPreselectCountry?: string }} */ (window);
    const preselectCode = w.__otterGeoPreselectCountry;
    const isAutoPopup = Boolean(preselectCode);

    if (preselectCode) {
      delete w.__otterGeoPreselectCountry;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#applyMarketSelection(preselectCode, host, isAutoPopup);
      });
    });
  };

  /**
   * Resolves the market option value following the precedence:
   * 1. Explicit user selection (sessionStorage)
   * 2. Detected country (override code, window, or sessionStorage)
   * 3. Normal store fallback
   * @param {string} [detectedIsoOverride]
   * @returns {{ value: string, isUserSelection: boolean, detectedIso: string | null } | null}
   */
  #resolveTargetMarketOption(detectedIsoOverride) {
    const { countries } = this.refs;
    if (!countries || !countries.options.length) return null;

    const options = Array.from(countries.options);

    // 1. Explicit user selection
    let userSelectedMarket = null;
    try {
      userSelectedMarket = sessionStorage.getItem('otter_user_selected_market');
    } catch (e) {}

    if (userSelectedMarket) {
      const match = options.find((opt) => opt.value === userSelectedMarket);
      if (match) {
        return { value: match.value, isUserSelection: true, detectedIso: null };
      }
    }

    // 2. Detected market
    let detectedIso = detectedIsoOverride;
    if (!detectedIso) {
      const w = /** @type {Window & { __otterGeoPreselectCountry?: string, __otterGeoDetectedCountry?: string }} */ (window);
      detectedIso = w.__otterGeoPreselectCountry || w.__otterGeoDetectedCountry;
    }
    if (!detectedIso) {
      try {
        detectedIso = sessionStorage.getItem('otter_geo_detected_country') || undefined;
      } catch (e) {}
    }

    if (detectedIso) {
      const detected = String(detectedIso).trim().toUpperCase();
      const marketIso = this.#findMarketKeyForCountry(detected);
      const exactMatch = this.#findExactCountryDropdownOption(detected);
      const marketExactMatch = marketIso ? this.#findExactCountryDropdownOption(marketIso) : null;
      const europeMatch =
        !exactMatch && !marketExactMatch && CountrySelectorComponent.#EUROPE_FALLBACK_ISO_CODES.has(detected)
          ? this.#findEuropeDropdownOption()
          : null;

      const match = exactMatch ?? marketExactMatch ?? europeMatch;
      const targetValue = match?.value ?? this.#findMarketCountryDropdownOption(detected)?.value;

      if (targetValue) {
        return { value: targetValue, isUserSelection: false, detectedIso: detected };
      }
    }

    // 3. Fallback
    const fallbackValue = this.#getStoreFallbackCountryOptionValue();
    if (fallbackValue) {
      return { value: fallbackValue, isUserSelection: false, detectedIso: null };
    }

    return null;
  }

  /**
   * @param {string} [detectedIsoOverride]
   * @param {HTMLElement} [dialogHost]
   * @param {boolean} [isAutoPopup=false]
   */
  #applyMarketSelection(detectedIsoOverride, dialogHost, isAutoPopup = false) {
    const { countries } = this.refs;
    if (!countries) return;

    const resolved = this.#resolveTargetMarketOption(detectedIsoOverride);
    if (!resolved) return;

    const { value: targetValue, isUserSelection, detectedIso } = resolved;
    const storeIso = String(window.Shopify?.country || '').trim().toUpperCase();

    // Guard 1: Direct match with active session country (auto-popup only)
    if (isAutoPopup && detectedIso && storeIso && detectedIso === storeIso) {
      this.#closeDialog(dialogHost);
      return;
    }

    this.#isProgrammaticChange = true;
    try {
      this.#syncSelectedAttribute(countries, targetValue);
      countries.value = targetValue;
      this.#onCountryChange();

      let restoredUserLanguage = false;
      if (isUserSelection) {
        try {
          const userLang = sessionStorage.getItem('otter_user_selected_language');
          if (userLang && this.refs.languages) {
            const match = Array.from(this.refs.languages.options).find((o) => o.value === userLang);
            if (match) {
              this.refs.languages.value = match.value;
              restoredUserLanguage = true;
            }
          }
        } catch (e) {}
      }

      if (!restoredUserLanguage) {
        this.#selectBestLanguageForBrowser();
      }

      if (isAutoPopup) {
        this.#guardGeoPreselect(targetValue);
      }
    } finally {
      this.#isProgrammaticChange = false;
    }
  }

  /** @param {HTMLElement} [dialogHost] */
  #closeDialog(dialogHost) {
    if (!dialogHost) return;

    // Try standard web component/dialog methods first
    if (typeof /** @type {any} */ (dialogHost).close === 'function') {
    /** @type {any} */ (dialogHost).close();
    } else if (typeof /** @type {any} */ (dialogHost).hide === 'function') {
    /** @type {any} */ (dialogHost).hide();
    }

    // Fallback: Click theme's explicit close button or remove open attribute
    const closeBtn = dialogHost.querySelector('button[type="reset"], .drawer__close, [data-dialog-close], .js-close-dialog');
    if (closeBtn instanceof HTMLElement) {
      closeBtn.click();
    } else {
      dialogHost.removeAttribute('open');
      dialogHost.classList.remove('is-open', 'active');
    }
  }

  #syncSelectedAttribute(select, targetValue) {
    for (const opt of Array.from(select.options)) {
      if (opt.value === targetValue) {
        opt.setAttribute('selected', 'selected');
        opt.selected = true;
      } else {
        opt.removeAttribute('selected');
        opt.selected = false;
      }
    }
  }

  #guardGeoPreselect(targetValue) {
    const { countries } = this.refs;
    if (!countries) return;

    if (this.#geoGuardCleanup) {
      this.#geoGuardCleanup();
      this.#geoGuardCleanup = null;
    }

    let userInteracted = false;
    const onUserChange = () => {
      userInteracted = true;
    };
    countries.addEventListener('change', onUserChange);

    const reapply = () => {
      if (userInteracted) return;
      if (countries.value === targetValue) return;
      this.#syncSelectedAttribute(countries, targetValue);
      countries.value = targetValue;
      this.#onCountryChange();
      this.#selectBestLanguageForBrowser();
    };

    const observer = new MutationObserver(reapply);
    observer.observe(countries, { childList: true, subtree: true, attributes: true });

    const timeouts = [
      setTimeout(reapply, 100),
      setTimeout(reapply, 500),
      setTimeout(reapply, 1500),
    ];

    const stopId = setTimeout(() => {
      this.#geoGuardCleanup?.();
    }, 2500);

    this.#geoGuardCleanup = () => {
      observer.disconnect();
      countries.removeEventListener('change', onUserChange);
      for (const id of timeouts) clearTimeout(id);
      clearTimeout(stopId);
      this.#geoGuardCleanup = null;
    };
  }

  /** @type {(() => void) | null} */
  #geoGuardCleanup = null;

  #getStoreFallbackCountryOptionValue() {
    const { countries } = this.refs;
    if (!countries) return null;

    const options = Array.from(countries.options);
    if (!options.length) return null;

    const codesFor = (/** @type {HTMLOptionElement} */ opt) =>
      opt.value
        .split('-')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);

    const w = /** @type {any} */ (window);
    const storeIso = String(w.Shopify?.country ?? w.OtterVars?.MarketSettings?.countryCode ?? '')
      .trim()
      .toUpperCase();

    if (storeIso) {
      const byStore = options.find((opt) => codesFor(opt).includes(storeIso));
      if (byStore) return byStore.value;
    }

    const serverSelected = options.find(
      (opt) => opt.selected || opt.getAttribute('aria-current') === 'true'
    );
    if (serverSelected) return serverSelected.value;

    return countries.value || null;
  }

  #findExactCountryDropdownOption(detectedRaw) {
    const { countries } = this.refs;
    if (!countries) return null;

    const detected = String(detectedRaw).trim().toUpperCase();
    if (!detected) return null;

    const options = Array.from(countries.options);
    const codesFor = (/** @type {HTMLOptionElement} */ opt) =>
      opt.value
        .split('-')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);

    const exact = options.find((opt) => codesFor(opt).includes(detected));
    return exact ? { value: exact.value } : null;
  }

  #findMarketCountryDropdownOption(detectedRaw) {
    const detected = String(detectedRaw).trim().toUpperCase();
    if (!detected) return null;

    const availableRaw = this.getAttribute('data-available-countries');
    if (!availableRaw) return null;

    const availableCodes = availableRaw
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    if (!availableCodes.includes(detected)) return null;

    const { countries } = this.refs;
    if (!countries) return null;

    const options = Array.from(countries.options);
    const availableOption = options.find((opt) => opt.classList.contains('is-available'));
    return availableOption ? { value: availableOption.value } : null;
  }

  /**
   * Finds the dropdown option representing the Europe market by its label, independent of
   * the ISO codes baked into its value. Used as a targeted fallback for European visitors.
   * @returns {{ value: string } | null}
   */
  #findEuropeDropdownOption() {
    const { countries } = this.refs;
    if (!countries) return null;

    const options = Array.from(countries.options);
    const europeOption = options.find((opt) =>
      /europe/i.test(opt.getAttribute('aria-label') || opt.textContent || '')
    );

    return europeOption ? { value: europeOption.value } : null;
  }

  /** @type {Map<string, string> | null} */
  #countryMarketIndex = null;

#findMarketKeyForCountry(countryCode) {
    if (!this.#countryMarketIndex) {
      this.#countryMarketIndex = new Map();
      try {
        let map = /** @type {any} */ (window).OtterVars?.MarketSettings?.countryToMarketMap;
        
        // Unroll double-encoded JSON strings safely
        while (typeof map === 'string') {
          map = JSON.parse(map);
        }

        if (map && typeof map === 'object') {
          for (const [key, value] of Object.entries(map)) {
            String(value).split(',').forEach((code) => {
              const iso = code.trim().toUpperCase();
              if (iso) this.#countryMarketIndex.set(iso, key);
            });
          }
        }
      } catch (e) {
        console.error('[CountrySelector Debug] Failed to build country→market index', e);
      }
    }

    const needle = String(countryCode).trim().toUpperCase();
    return this.#countryMarketIndex.get(needle) ?? null;
  }

  #selectBestLanguageForBrowser() {
    const { languages } = this.refs;
    if (!languages || languages.options.length === 0) return;

    const navFull = (navigator.language || '').replace('_', '-').toLowerCase();
    const navPrimary = navFull.split('-')[0] || 'en';

    const opts = Array.from(languages.options);
    const exactFull = opts.find((o) => o.value.toLowerCase() === navFull);
    if (exactFull) {
      languages.value = exactFull.value;
      return;
    }

    const primary = opts.find((o) => o.value.toLowerCase().split('-')[0] === navPrimary);
    if (primary) {
      languages.value = primary.value;
    }
  }

  #onCountryChange = () => {
    this.#updateLanguageOptions();
    this.#updateCurrencySelection();
  };

  #onCurrencyChange = () => {
    const { currencies } = this.refs;
    if (!currencies) return;

    const selectedOption = currencies.options[currencies.selectedIndex];
    if (!selectedOption) return;

    const currencyCode = selectedOption.getAttribute('data-currency-code');

    if (typeof utag !== 'undefined') {
      utag.link({
        tealium_event: 'currency_change',
        currency_code: currencyCode,
      });
    } else {
    }
  };

  #checkUrlCurrencyParam() {
    const { currencies, countryInput, languageInput, form } = this.refs;
    if (!currencies || !form) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('locale')) return;
    const urlCurrency = urlParams.get('currency');
    if (!urlCurrency) return;

    const upperCurrency = urlCurrency.toUpperCase();
    const activeCurrency = this.#getActiveCurrency();

    if (upperCurrency === activeCurrency) return;

    const matchingOption = Array.from(currencies.options).find(
      (opt) => opt.getAttribute('data-currency-code') === upperCurrency
    );

    if (!matchingOption) return;

    currencies.value = matchingOption.value;
    countryInput.value = matchingOption.value;
    languageInput.value =
      this.refs.languages?.value ||
      document.documentElement.lang?.split('-')[0] ||
      'en';

    this.setCookie('declined_redirect', 'true', { days: 30 });

    if (typeof utag !== 'undefined') {
      utag.link({
        tealium_event: 'currency_change',
        currency_code: upperCurrency,
      });
    }

    form.submit();
  }

  #checkCacheMismatch() {
    const { countries, currencies } = this.refs;
    if (!countries) return;

    if (this.#geoGuardCleanup) return;
    try {
      if (
        sessionStorage.getItem('otter_user_selected_market') ||
        sessionStorage.getItem('otter_geo_detected_country')
      ) {
        return;
      }
    } catch (e) {}

    const sessionCountry = String(window.Shopify?.country || '').trim().toUpperCase();
    const sessionCurrency = window.Shopify?.currency?.active;
    if (!sessionCountry) return;

    const selectedOption = countries.options[countries.selectedIndex];
    const renderedCountry = selectedOption?.value;

    // 1. Correct Country Select
    if (renderedCountry && renderedCountry !== sessionCountry) {
      const correctCountryOpt = Array.from(countries.options).find((opt) => {
        const codes = opt.value.split('-').map((s) => s.trim().toUpperCase());
        return codes.includes(sessionCountry);
      });

      if (correctCountryOpt) {
        this.#syncSelectedAttribute(countries, correctCountryOpt.value);
        countries.value = correctCountryOpt.value;
        this.#onCountryChange();
      }
    }

    // 2. Correct Currency Select
    if (currencies && sessionCurrency) {
      const activeCurrencyOpt = currencies.options[currencies.selectedIndex];
      const renderedCurrency = activeCurrencyOpt?.getAttribute('data-currency-code');

      if (renderedCurrency && renderedCurrency !== sessionCurrency) {
        const correctCurrencyOpt = Array.from(currencies.options).find(
          (opt) => opt.getAttribute('data-currency-code') === sessionCurrency
        );
        if (correctCurrencyOpt) {
          currencies.value = correctCurrencyOpt.value;
        }
      }
    }

    // 3. Auto-close dialog if visitor is on their matching session country
    if (countries.value === sessionCountry) {
      const dialogHost = this.closest('#drawer-country-language-dialog') ||
        document.getElementById('drawer-country-language-dialog');
      if (dialogHost) {
        this.#closeDialog(dialogHost);
      }
    }
  }

  #getActiveCurrency() {
    const fromShopify = window.Shopify?.currency?.active;
    if (fromShopify) return fromShopify;

    const { currencies } = this.refs;
    if (currencies) {
      return (
        currencies.options[currencies.selectedIndex]?.getAttribute('data-currency-code') || ''
      );
    }
    return '';
  }

  #updateLanguageOptions() {
    const { countries, languages } = this.refs;
    if (!countries || !languages) return;

    const previousLanguageValue = languages.value;
    const selectedOption = countries.options[countries.selectedIndex];
    if (!selectedOption) return;

    /** @type {{iso_code: string, name: string, url?: string}[]} */
    let availableLanguages = [];
    try {
      const rawData = selectedOption.getAttribute('data-languages');
      availableLanguages = JSON.parse(rawData || '[]');
    } catch (e) {
      console.error("[CountrySelector Debug] Failed to parse language data", e);
      return;
    }

    languages.innerHTML = '';
    const fragment = document.createDocumentFragment();
    let didRestorePreviousLanguage = false;

    availableLanguages.forEach((lang) => {
      const option = new Option(lang.name, lang.iso_code);
      if (lang.url) {
        option.setAttribute('data-url', lang.url);
      }
      if (lang.iso_code === previousLanguageValue) {
        option.selected = true;
        didRestorePreviousLanguage = true;
      }
      fragment.appendChild(option);
    });

    languages.appendChild(fragment);

    if (!didRestorePreviousLanguage && languages.options.length > 0) {
      languages.selectedIndex = 0;
    }
  }

  #updateCurrencySelection() {
    const { countries, currencies } = this.refs;
    if (!countries || !currencies) return;

    const selectedOption = countries.options[countries.selectedIndex];
    if (!selectedOption) return;

    const currenciesData = selectedOption.getAttribute('data-currencies');
    if (!currenciesData) return;

    const currencyList = currenciesData.split(',').map((c) => c.trim()).filter(Boolean);
    const activeCurrency = this.#getActiveCurrency();
    const countryIso = selectedOption.value;

    currencies.innerHTML = '';
    const fragment = document.createDocumentFragment();

    currencyList.forEach((currencyCode) => {
      const opt = document.createElement('option');
      opt.value = countryIso;
      opt.setAttribute('data-currency-code', currencyCode);
      opt.textContent = currencyCode;
      if (currencyCode === activeCurrency) {
        opt.selected = true;
      }
      fragment.appendChild(opt);
    });

    if (!currencyList.includes(activeCurrency) && fragment.firstChild) {
      /** @type {HTMLOptionElement} */ (fragment.firstChild).selected = true;
    }

    currencies.appendChild(fragment);

    const currencyWrapper = currencies.closest('.custom-select');
    if (currencyWrapper) {
      if (currencyList.length > 1) {
        currencyWrapper.classList.remove('hidden');
      } else {
        currencyWrapper.classList.add('hidden');
      }
    }
  }

  submit = (event) => {
    event.preventDefault();
    const { languageInput, countryInput, countries, languages, currencies, form } = this.refs;

    this.setCookie('declined_redirect', 'true', { days: 30 });
    try {
      if (countries?.value) {
        sessionStorage.setItem('otter_user_selected_market', countries.value);
      }
      if (languages?.value) {
        sessionStorage.setItem('otter_user_selected_language', languages.value);
      }
    } catch (e) {}
    this.#onCountryChange();

    const selectedCountryOption = countries.options[countries.selectedIndex];
    const selectedLanguageOption = languages.options[languages.selectedIndex];
    const isCrossStore =
      selectedCountryOption &&
      !selectedCountryOption.classList.contains('is-available') &&
      countries.value !== window.Shopify?.country;

    let selectedCurrency = null;
    let hasMultipleCurrencies = false;
    if (currencies) {
      const selectedCurrencyOption = currencies.options[currencies.selectedIndex];
      selectedCurrency = selectedCurrencyOption?.getAttribute('data-currency-code');
      const currencyWrapper = currencies.closest('.custom-select');
      hasMultipleCurrencies = currencyWrapper && !currencyWrapper.classList.contains('hidden');
    }

    if (isCrossStore) {
      const redirectUrl = selectedLanguageOption?.getAttribute('data-url');
      if (redirectUrl) {
        const currentUrl = new URL(window.location.href);
        const normalizedBaseUrl = /^https?:\/\//i.test(redirectUrl)
          ? redirectUrl
          : `https://${redirectUrl}`;
        const baseUrl = new URL(normalizedBaseUrl);
        const basePath = baseUrl.pathname.replace(/\/+$/, '');

        const localePrefix = /** @type {any} */ (window).OtterVars?.MarketSettings?.rootUrl?.replace(/\/+$/, '') || '';
        let currentPath = currentUrl.pathname;
        if (localePrefix.length > 1 && currentPath.toLowerCase().startsWith(localePrefix.toLowerCase())) {
          currentPath = currentPath.slice(localePrefix.length);
        }
        currentPath = currentPath.replace(/^\/+/, '');

        const combinedPath = [basePath, currentPath].filter(Boolean).join('/');
        const targetPathname = combinedPath ? (combinedPath.startsWith('/') ? combinedPath : `/${combinedPath}`) : '/';
        const url = new URL(baseUrl.origin);
        url.pathname = targetPathname;
        url.search = currentUrl.search;
        url.hash = currentUrl.hash;
        if (hasMultipleCurrencies && selectedCurrency) {
          url.searchParams.set('currency', selectedCurrency);
        }
        window.location.href = url.toString();
      }
      return;
    }

    if (hasMultipleCurrencies && selectedCurrency) {
      const activeCurrency = this.#getActiveCurrency();
      if (selectedCurrency !== activeCurrency) {
        const url = new URL(window.location.href);
        url.searchParams.set('currency', selectedCurrency);
        window.location.href = url.toString();
        return;
      }
    }

    languageInput.value = languages.value;
    countryInput.value = countries.value;
    form?.submit();
  };

  setCookie = (name, value, options = {}) => {
    const { days, path = '/', domain, secure, sameSite = 'Lax' } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (typeof days === 'number') {
      const date = new Date();
      date.setTime(date.getTime() + days * 864e5);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (path) cookieString += `; path=${path}`;
    if (domain) cookieString += `; domain=${domain}`;
    if (secure) cookieString += '; secure';
    if (sameSite) cookieString += `; samesite=${sameSite}`;

    document.cookie = cookieString;
  };
}

if (!customElements.get('country-selector-component')) {
  customElements.define('country-selector-component', CountrySelectorComponent);
}